import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { apiGet } from "api/callApi";
import { Select, Checkbox, ConfigProvider } from "antd";
import theme from "theme";
import SC from "./style";
import TooltipText from "components/TooltipText";

const LinkOccupantDropdown = ({
  onChange = () => {},
  defaultValue = [],
  name,
  disabledDropdown = false,
  placeholder = "Select Occupant",
  projectId,
  type,
  width = "100%",
  maxHeight,
  maxTagCount = 3,
}) => {
  const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || {}
  );

  useEffect(() => {
    const getOptionData = async () => {
      try {
        const endpoint = `/v1/getoccupantoptions?projectId=${currentProject.id}`;
        const response = await apiGet(endpoint, false);
        const fetchedOptions = response.data
          .filter((item) => item.id != projectId)
          .map((item) => ({
            id: item.id,
            label: item.occupantName,
          }));
        setOptions(fetchedOptions);
        const initialSelected = fetchedOptions.filter((opt) =>
          defaultValue.includes(opt.id)
        );
        setSelectedValues(initialSelected);
        setIsSelectAll(
          initialSelected.length === fetchedOptions.length &&
            fetchedOptions.length > 0
        );
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    getOptionData();
  }, [currentProject.id, projectId, type]);

  const handleOptionChange = (option, checked) => {
    const updatedValues = checked
      ? [...selectedValues, option]
      : selectedValues.filter((value) => value.id !== option.id);

    setSelectedValues(updatedValues);
    setIsSelectAll(updatedValues.length === options.length);
    setSearchValue("");

    onChange(
      name,
      updatedValues.map((value) => value.id)
    );
  };

  const handleSelectChange = (selectedIds) => {
    const updatedValues = options.filter((option) =>
      selectedIds.includes(option.id)
    );
    setSelectedValues(updatedValues);
    setIsSelectAll(updatedValues.length === options.length);
    // Preserve search value after selection
    setSearchValue("");
    onChange(
      name,
      updatedValues.map((value) => value.id)
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedValues(options);
      onChange(
        name,
        options.map((value) => value.id)
      );
    } else {
      setSelectedValues([]);
      onChange(name, []);
    }
    setIsSelectAll(checked);
  };

  const handleSearch = (value) => {
    setSearchValue(value); // Update search value normally
  };

  const filteredOptions = searchValue
    ? options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          selectedValues.some((selected) => selected.id === opt.id) // Keep selected values visible
      )
    : options; // If no search, show all options

  const renderCheckboxList = () => (
    <>
      {filteredOptions.length === 0 ? (
        <span style={{ marginLeft: "10px" }}>No occupants found</span>
      ) : (
        <>
          <Checkbox
            checked={isSelectAll}
            onChange={(e) => handleSelectAll(e.target.checked)}
            style={{
              display: "flex",
              padding: "5px 10px",
              borderBottom: "1px solid #d9d9d9",
              fontWeight: "bold",
            }}
          >
            Select All
          </Checkbox>
          <SC.ChildContainer maxHeight={maxHeight}>
            {filteredOptions.map((option, index) => (
              <React.Fragment key={option.id}>
                <Checkbox
                  checked={selectedValues.some(
                    (value) => value.id === option.id
                  )}
                  onChange={(e) => handleOptionChange(option, e.target.checked)}
                  style={{
                    display: "flex",
                    padding: "5px 10px",
                    borderBottom: "1px solid #d9d9d9",
                  }}
                >
                  <TooltipText title={option.label} maxLength={80} />
                </Checkbox>
              </React.Fragment>
            ))}
          </SC.ChildContainer>
        </>
      )}
    </>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#ED850C",
          colorBorder: theme.colors.bordertask,
          colorText: "black",
          colorTextPlaceholder: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Select
        mode="multiple"
        value={selectedValues.map((value) => value.id)}
        onChange={handleSelectChange}
        style={{
          width: width,
        }}
        placeholder={placeholder}
        dropdownRender={renderCheckboxList}
        disabled={disabledDropdown}
        maxTagCount={maxTagCount}
        onSearch={handleSearch} // Preserve search input
      >
        {filteredOptions.map((option) => (
          <Select.Option key={option.id} value={option.id}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </ConfigProvider>
  );
};

export default LinkOccupantDropdown;
